import {
    Bell,
    LayoutDashboard,
    Lock,
    LogOut,
    Moon,
    Newspaper,
    Plug,
    Settings,
    Sun,
    User,
    Wallet,
} from 'lucide-react';
import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Logo from '../Logo';
import { useGate } from '../../contexts/GateContext';

const Divider = () => <div className="my-2 border-t border-edge/80" />;

/**
 * Dashboard sidebar — public items first, then personalization behind login.
 */
const SidebarNav = () => {
    const { user, logout, isAuthenticated } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const { openAuthGate } = useGate();
    const location = useLocation();
    const navigate = useNavigate();

    const isActive = (path) => {
        if (path === '/dashboard') {
            return location.pathname === '/dashboard';
        }
        return location.pathname === path || location.pathname.startsWith(`${path}/`);
    };

    const go = (path, { locked }) => {
        if (locked && !isAuthenticated) {
            openAuthGate('Sign up to track wallets and create alerts.');
            return;
        }
        navigate(path);
    };

    /** Guests with `locked` see a lock + gate; everyone else gets a normal link. */
    const NavRow = ({ href, icon: Icon, label, locked = false, badge = null }) => {
        const active = isActive(href);
        const guestLocked = locked && !isAuthenticated;

        const linkBase = `group flex min-w-0 flex-1 items-center px-2 py-1.5 text-sm font-medium rounded-md transition-all duration-200 ${
            active
                ? 'bg-blue-600 text-white shadow-md'
                : guestLocked
                    ? 'text-gray-500 dark:text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800/80'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'
        }`;

        if (guestLocked) {
            return (
                <button type="button" onClick={() => go(href, { locked })} className={`${linkBase} w-full text-left`}>
                    <Icon size={16} className="mr-2 flex-shrink-0 opacity-70" />
                    <span className="flex-1 truncate">{label}</span>
                    {badge && (
                        <span className="mr-1 rounded-full bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 text-[10px] font-semibold text-content-muted">
                            {badge}
                        </span>
                    )}
                    <Lock size={14} className="ml-1 flex-shrink-0 opacity-60" />
                </button>
            );
        }

        return (
            <Link to={href} className={linkBase}>
                <Icon size={16} className={`mr-2 flex-shrink-0 ${active ? '' : 'group-hover:scale-105 transition-transform duration-200'}`} />
                <span className="truncate">{label}</span>
                {badge && (
                    <span className="ml-auto rounded-full bg-brand-light px-1.5 py-0.5 text-[10px] font-semibold text-brand-dark">
                        {badge}
                    </span>
                )}
            </Link>
        );
    };

    return (
        <div className="flex h-screen w-60 flex-col border-r border-edge bg-surface-card">
            <Link
                to="/"
                title="Back to landing page"
                className="flex h-14 items-center justify-center border-b border-edge hover:bg-surface-secondary transition-colors"
            >
                <Logo size="small" />
            </Link>

            <nav className="flex-1 space-y-0.5 px-2 py-2 overflow-y-auto">
                <p className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-content-muted">Discover</p>
                <NavRow href="/dashboard" icon={LayoutDashboard} label="Overview" />
                <NavRow href="/dashboard/news" icon={Newspaper} label="News" />

                <Divider />

                <p className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-content-muted">Monitor</p>
                <NavRow href="/dashboard/watchlist" icon={Wallet} label="Watchlist" locked />
                <NavRow href="/dashboard/alerts" icon={Bell} label="Alerts" locked />

                <Divider />

                <p className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-content-muted">Workspace</p>
                <NavRow href="/dashboard/integrations" icon={Plug} label="Integrations" locked />
                <NavRow href="/dashboard/settings" icon={Settings} label="Settings" locked />
            </nav>

            <div className="border-t border-edge p-2 space-y-1">
                <button
                    type="button"
                    onClick={toggleTheme}
                    className="flex items-center w-full px-2 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-all duration-200 group"
                >
                    {theme === 'dark' ? (
                        <Sun size={16} className="mr-2 text-yellow-500 group-hover:scale-105 transition-transform" />
                    ) : (
                        <Moon size={16} className="mr-2 text-blue-500 group-hover:scale-105 transition-transform" />
                    )}
                    {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
                </button>

                {user && (
                    <div className="space-y-1">
                        <div className="flex items-center px-2 py-1.5 bg-gray-50 dark:bg-gray-800 rounded-md">
                            <div className="flex items-center justify-center w-7 h-7 bg-emerald-500 rounded-full mr-2">
                                <User size={14} className="text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-xs font-semibold text-content-primary truncate">{user.name || 'User'}</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user.email}</p>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={logout}
                            className="flex items-center w-full px-2 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-all duration-200 group"
                        >
                            <LogOut size={16} className="mr-2 group-hover:scale-105 transition-transform" />
                            Sign Out
                        </button>
                    </div>
                )}

                {!isAuthenticated && (
                    <div className="px-2 py-2 space-y-1">
                        <Link
                            to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`}
                            className="block w-full text-center rounded-lg bg-blue-600 text-white text-xs font-semibold py-2 hover:bg-blue-700"
                        >
                            Log In
                        </Link>
                        <Link
                            to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`}
                            className="block w-full text-center rounded-lg border border-gray-300 dark:border-gray-600 text-xs font-semibold py-2 text-gray-800 dark:text-gray-100"
                        >
                            Sign Up
                        </Link>
                    </div>
                )}

            </div>
        </div>
    );
};

export default SidebarNav;
